import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { hasAnyRole, ROLES, type Role } from './roles';
import { useAuth } from './useAuth';
import { useViewingAs } from './useViewingAs';

interface RequireRoleProps {
  /** The user needs at least one of these. Super Admin also satisfies Admin. */
  allow: readonly Role[];
}

/**
 * A route guard. Used as a layout route: any child routes render only when the
 * current user holds one of the allowed roles.
 *
 * The rejected case redirects to /forbidden rather than rendering a message in
 * place, and records where the user was trying to go. That is what lets a real
 * sign-in screen send them onward to the right page afterwards.
 *
 * This only controls what the portal displays. It is not a security boundary:
 * the backend must check permissions on every request as well, because anyone
 * can call the API directly.
 */
export function RequireRole({ allow }: RequireRoleProps) {
  const { user } = useAuth();
  const { viewing } = useViewingAs();
  const location = useLocation();

  /*
   * The one exception: Activist staff viewing an organization may open that
   * organization's sections, read-only. Checked against the staff role first,
   * so it can only ever widen what staff see - an organization user gains
   * nothing from it, and staff-only routes are untouched because their allow
   * list names no organization role.
   */
  const viewingOrganizationSection =
    user?.role === ROLES.ACTIVIST_STAFF &&
    viewing !== null &&
    (allow.includes(ROLES.SUPER_ADMIN) || allow.includes(ROLES.ADMIN));

  if (!viewingOrganizationSection && !hasAnyRole(user?.role ?? null, allow)) {
    return <Navigate to="/forbidden" replace state={{ from: location.pathname }} />;
  }

  return <Outlet />;
}
